import React, {
  useEffect,
  useState,
  ChangeEvent,
  useCallback,
  useRef, useMemo
} from "react";
import "./User.scss";
import { BusinessPurchaseSection } from './sections/BusinessPurchaseSection';
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  Row,
  Anchor,
} from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Billing from "./Billing/Billing";
import Places from "./Places/Places";
import Cart from "../Cart/Cart";
import { CartData } from "../../interfaces/CartData";
import { useCart } from "../../services/cartService";
import { ShippingOption } from "../../interfaces/ShippingOption";
import { useHeaderMenu } from "../../contexts/login.context";
import nvCookieHelpers from "../../services/cookieService";
import useLoader from "../../contexts/LoaderContext";
// @ts-ignore
import cartLoader from "../../assets/images/cart_Loader.gif";
import { useTranslation } from "react-i18next";
import { config } from "../../config";
import { formatPhoneNo, isJwtTokenExpired } from "../../helpers/common";
import { initialize } from "@nv-ces/ucp-wrapper";
import { v4 as uuidv4 } from "uuid";
import { PaymentEvents } from "./constants";
import * as intrfce from "./Interfaces";
import { useLocalization } from "../../contexts/LocalizationContext";
import BillingJapan from "./Billing/BillingJapan";
import PlacesJapan from "./Places/PlacesJapan";
import AddressModal from "./AddressModal/AddressModal";
import CheckoutStep2 from "./CheckoutStep2";
import { useCartSuccess } from "./hooks/useCartSuccess";
import { useAddressHandlers } from "./hooks/useAddressHandlers";
import { useAddressModal } from "./hooks/useAddressModal";
import {
  createBillingAddressObject,
  createShippingAddressObject,
} from "./utils/addressUtils";
import {
  isObjectEmpty,
  isEmptyAddress,
  createNewAddressPlaceholder,
  createAddressFromCartData,
  savedAddressToFormAddress,
  createEmptyFormAddress,
  createEmptyBillingData,
  updateAddressWithData,
  createTemporaryAddress,
  validateFormFields,
  hasMeaningfulAddressFields,
  handleNewAddressPlaceholder
} from "./utils";
import { validateField, ValidationContext } from "./validation";
import { useAddressState } from "./useAddressState";
import { useCartInitialization } from "./useCartInitialization";

const User = () => {
  const { userType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const RESET_ADDRESS = "reset";
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderState = queryParams.get("order");
  const stepParam = queryParams.get("step");
  const isVerify: boolean = false;
  const isThanks: boolean = false;
  const [isBusinessPurchase, setIsBusinessPurchase] = useState(false);
  
  // Multi-step checkout state - initialize from query param if present
  const [currentStep, setCurrentStep] = useState<1 | 2>(() => {
    return stepParam === "2" ? 2 : 1;
  });

  const { locale, localeWith_, stateDropdown, countryList, currencySign, countyField, defaultCurrency } = useLocalization();

  // Consolidated address state management using custom hook
  const {
    selectedBillingAddress,
    setSelectedBillingAddress,
    selectedShippingAddress,
    setSelectedShippingAddress,
    savedBillingAddress,
    setSavedBillingAddress,
    savedShippingAddress,
    setSavedShippingAddress,
    formBillingAddress,
    setFormBillingAddress,
    formShippingAddress,
    setFormShippingAddress,
    formBillingData,
    setFormBillingData,
    isBillingReadonly,
    setIsBillingReadonly,
    isShippingReadonly,
    setIsShippingReadonly,
    isShipping,
    setIsShipping,
    temporaryAddress,
    setTemporaryAddress,
    recommendedShippingAddress,
    setRecommendedShippingAddress,
    selectedAddressOption,
    setSelectedAddressOption,
  } = useAddressState(countryList, locale);
  
  // Ref to track if we're in restricted shipping error state
  const hasRestrictedShippingError = useRef(false);
  // Ref to prevent useEffect from auto-loading addresses right after error
  const skipAddressAutoLoad = useRef(false);
  const [couponCode, setCoupon] = useState<string | null>(null);
  const futureUse = true;
  const [cartData, setCartData] = useState<CartData | undefined>();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
    >({});
  const [enableReviewOrderBtn, setEnableReviewOrderBtn] =
    useState<boolean>(false);
  const {
    showBodyLoader,
    hideBodyLoader,
    isCartLoading,
    hideCartLoader,
    showCartLoader,
  } = useLoader();
  const [promoErr, setPromoErr] = useState<boolean>(false);
  const [addressErr, setAddressErr] = useState<null | string>(null);
  const [savePayment, setSavePayment] = useState(false);
  const {
    setItemsInCart,
    order,
    setOrder,
    brand,
    setBrand
  } = useHeaderMenu();
  const [paymentEventMessage, setPaymentEventMessage] = useState<string | null>(
    null
  );
  const [validPayment, setValidPayment] = useState<boolean>(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressValidationResult, setAddressValidationResult] =
    useState<any>(null);
  // State for dual address validation (billing + shipping)
  const [billingValidationResult, setBillingValidationResult] = useState<any>(null);
  const [shippingValidationResult, setShippingValidationResult] = useState<any>(null);
  const [recommendedBillingAddress, setRecommendedBillingAddress] = useState<intrfce.iAddressFormData | null>(null);
  const [temporaryBillingAddress, setTemporaryBillingAddress] = useState<intrfce.iSavedAddress | null>(null);
  const [selectedBillingAddressOption, setSelectedBillingAddressOption] = useState<"yourAddress" | "recommendedAddress">("yourAddress");
  // State to track which fields differ from user's input
  const [billingAddressDifferences, setBillingAddressDifferences] = useState<string[]>([]);
  const [shippingAddressDifferences, setShippingAddressDifferences] = useState<string[]>([]);

  // Memoize address builder options to avoid recreating on every render
  const addressOptions = useMemo(() => ({
    locale,
    stateDropdown,
    countyField
  }), [locale, stateDropdown, countyField]);

  // Helper function for consistent state value handling across the component
  const getStateValue = useCallback((stateValue: string, fallback?: string) => {
    if (stateDropdown || locale === 'zh-tw') {
      return locale === 'zh-tw' ? "TW" : (stateValue || fallback);
    }
    return fallback;
  }, [stateDropdown, locale]);

  if (userType) {
    sessionStorage.setItem("customer", userType);
  } else {
    // Handle the case when userType is undefined
    navigate(`/${locale}/cart/`);
  }

  const handleLogin = useCallback(() => {
    window.location.href = `${config.bcSSOLoginUrl}${locale}/cart`;
  }, [locale]);

  const reviewOrder = useCallback((user: string) => {
    navigate(`/${locale}/cart/verify/`, { state: { user, validationErrors } });
  }, [locale, navigate, validationErrors]);

  const emptyCart = useCallback((msg: string, pid?: number) => {
    navigate(`/${locale}/cart/empty`, { state: { msg, pid } });
  }, [locale, navigate]);

  const [shippingOptions, setShippingOptions] =
    useState<intrfce.iShippingDetails>({
      shippingOption: 0
    });
  const [shippingList, setShippingList] = useState<ShippingOption[]>([]);
  const [formTaxData, setFormTaxData] = useState<intrfce.iTaxFormData>({
    companyName: "",
    vatNo: "",
  });
  const [formData, setFormData] = useState<intrfce.iFormContainer>({
    firstNameShiping: "",
    lastNameShiping: "",
    contactShiping: "",
    iBillingFormData: formBillingData,
    iBillingFormAddress: formBillingAddress,
    iShippingDetails: shippingOptions,
    iShippingFormAddress: formShippingAddress,
  });

  const validateForm = useCallback(
    (
      fieldName: string,
      value: string | {} | boolean,
      formType: "billing" | "shipping" | ""
    ): boolean => {
      const validationContext: ValidationContext = {
        locale,
        userType,
        stateDropdown,
        formBillingData,
        formBillingAddress,
        formShippingAddress,
        formTaxData,
        shippingOptions,
        t,
      };

      const error = validateField(fieldName, value, formType, validationContext);

      // Update validation errors in state
      // For addressBilling and addressShipping, use the fieldName as-is
      if (fieldName === "addressBilling" || fieldName === "addressShipping") {
        // Map to the format expected by the UI: billingaddress or shippingaddress
        const errorFieldName = formType === "billing" ? "billingaddress" : "shippingaddress";
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [errorFieldName]: error,
        }));
      } else if (
        fieldName === "pincode" ||
        fieldName === "address" ||
        fieldName === "cityValue" ||
        fieldName === "stateValue"
      ) {
        let errorFieldName = formType + fieldName;
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [errorFieldName]: error,
        }));
      }
      if (fieldName !== "promoCode" && fieldName !== "addressBilling" && fieldName !== "addressShipping") {
        setValidationErrors((prevErrors) => ({
          ...prevErrors,
          [fieldName]: error,
        }));
      }

      return error.length === 0;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, formBillingData, formBillingAddress, formShippingAddress, shippingOptions, setValidationErrors, locale, t, userType, formTaxData]
  );
  const orderAccount = useCallback(() => {
    window.location.href = config.bcOrdersUrl;
  }, []);

  const isBillingAddressValid = useCallback((): boolean => {
    // First check if the address has meaningful fields beyond country selection
    if (!hasMeaningfulAddressFields(formBillingAddress, locale)) {
      return false;
    }

    // Validate all fields with locale-specific skip rules
    return validateFormFields(
      formBillingAddress,
      "billing",
      validateForm,
      validationErrors,
      { skipStateValueForNonUS: true, locale }
    );
  }, [formBillingAddress, locale, validateForm, validationErrors]);

  const isBillingFormValid = useCallback((): boolean => {
    return validateFormFields(
      formBillingData,
      "billing",
      validateForm,
      validationErrors
    );
  }, [formBillingData, validateForm, validationErrors]);

  const isTaxDataValid = useCallback((): boolean => {
    // Only validate tax data if business purchase is checked
    if (!isBusinessPurchase) {
      return true;
    }

    // Check if both fields are valid (no errors)
    if (formTaxData.companyName || formTaxData.vatNo) {
      // If either field has a value, check for errors
      if (validationErrors.companyName || validationErrors.vatNo) {
        return false;
      }
    }
    return true;
  }, [isBusinessPurchase, formTaxData, validationErrors.companyName, validationErrors.vatNo]);

  const isShippingAddressValid = useCallback((): boolean => {
    return validateFormFields(
      formShippingAddress,
      "shipping",
      validateForm,
      validationErrors
    );
  }, [formShippingAddress, validateForm, validationErrors]);

  const isShippingFormValid = useCallback((): boolean => {
    return validateFormFields(
      formData,
      "shipping",
      validateForm,
      validationErrors
    );
  }, [formData, validateForm, validationErrors]);

  // Check if Continue button should be enabled (Step 1)
  const [isContinueButtonEnabled, setIsContinueButtonEnabled] = useState(false);
  
  useEffect(() => {
    try {
      // Billing form and address must be valid
      const billingValid = isBillingFormValid() && isBillingAddressValid() && isTaxDataValid();
      
      // If shipping to different address, shipping must also be valid
      const shippingValid = !isShipping || (isShippingFormValid() && isShippingAddressValid());
      
      setIsContinueButtonEnabled(billingValid && shippingValid);
    } catch (error) {
      console.error('Error checking button enabled state:', error);
      setIsContinueButtonEnabled(false);
    }
  }, [
    formBillingData,
    formBillingAddress,
    formShippingAddress,
    formData,
    formTaxData,
    isBusinessPurchase,
    isShipping,
    locale,
    validationErrors,
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isShippingFormValid,
    isShippingAddressValid
  ]);

  const handleError = useCallback((str: string, pid?: number) => {
    if (pid) {
      if (str && str.length) {
        emptyCart(str, pid);
      } else {
        emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"), pid);
      }
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
    setItemsInCart(0);
    hideBodyLoader();
  }, [emptyCart, t, hideBodyLoader, setItemsInCart]);
  const handleCouponError = useCallback((str: string) => {
    if (str && str.length) {
      emptyCart(str);
    }
    if (couponCode && couponCode.length >= 0) {
      setPromoErr(true);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: t("INVALID_PROMO"),
      }));
    } else if (!couponCode) {
      setPromoErr(true);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: t("INVALID_PROMO"),
      }));
    } else {
      setPromoErr(false);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: "",
      }));
    }
    hideBodyLoader();
  }, [couponCode, emptyCart, hideBodyLoader, t]);
  const { getCart, updateCart, submitCart, submitAddress } =
    useCart();

  const fetchCart = async () => {
    showBodyLoader();
    await getCart(handleSuccess, handleError, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const fieldNameRef = useRef<string | null>(null);
  const handleBillingInputChange = (
    fieldName: string,
    value: string | boolean
  ) => {
    setFormBillingData((prevData) => ({ ...prevData, [fieldName]: value }));
    fieldNameRef.current = fieldName;
    setFormData((prevData) => ({
      ...prevData,
      ...(fieldName === "firstNameBilling" &&
        !isShipping &&
        typeof value === "string"
        ? { firstNameShiping: value }
        : {}),
      ...(fieldName === "lastNameBilling" &&
        !isShipping &&
        typeof value === "string"
        ? { lastNameShiping: value }
        : {}),
      ...(fieldName === "contactBilling" &&
        !isShipping &&
        typeof value === "string"
        ? { contactShiping: value }
        : {}),
    }));
  };

  const ucpInitialized = useRef(false);
  const isLoggedIn =
    nvCookieHelpers.getCookie("isUserLoggedIn") === "true" ? true : false;

  // Create refs to store form field values
  const formFieldsRef = useRef<{
    billing: {
      address: intrfce.iAddressFormData | null;
      data: intrfce.iBillingFormData | null;
    };
    shipping: {
      address: intrfce.iAddressFormData | null;
      data: intrfce.iFormContainer | null;
    };
  }>({
    billing: {
      address: null,
      data: null,
    },
    shipping: {
      address: null,
      data: null,
    },
  });

  // Handle step query parameter changes (e.g., when returning from review page)
  useEffect(() => {
    const stepParam = queryParams.get("step");
    if (stepParam === "2" && currentStep !== 2) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (stepParam === "1" && currentStep !== 1) {
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Monitor page visibility to detect when page becomes active again
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When page becomes visible again, check if token is expired
      if (!document.hidden && cartData?.idt) {
        if (isJwtTokenExpired(cartData.idt)) {
          console.warn("JWT token (idt) is expired after page became active. Refreshing page to reinitialize.");
          window.location.reload();
        }
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [cartData?.idt]);

  // Initialize UCP without state updates
  useEffect(() => {
    if (!ucpInitialized.current && cartData) {
      // Check if idt token exists and is expired
      if (cartData?.idt && isJwtTokenExpired(cartData.idt)) {
        console.warn("JWT token (idt) is expired. Refreshing page to reinitialize.");
        // Refresh the page to reinitialize everything with a fresh token
        window.location.reload();
        return;
      }

      ucpInitialized.current = true;
      let originEnvironment = window.location.origin
        .split("/")[2]
        .split("-")[0]
        ?.toUpperCase();
      originEnvironment =
        originEnvironment !== "STG" && originEnvironment !== "DEV"
          ? "PROD"
          : originEnvironment;

      // Store initial form values in ref
      formFieldsRef.current = {
        billing: {
          address: formBillingAddress,
          data: formBillingData,
        },
        shipping: {
          address: formShippingAddress,
          data: formData,
        },
      };
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        paymentSrc: '',
      }));
      initialize({
        version: "v1",
        environment: originEnvironment as any,
        clientId: "nvstore",
      })
        .then((Ucp) => {
          if (isLoggedIn && cartData?.idt) {
            window.Ucp?.events?.addEventListener(
              "event-registered-user-payment-methods",
              (event: any) => {
                if (event.detail.message === PaymentEvents.Exists || event.detail.message === PaymentEvents.Changed) {
                  setValidPayment(true);
                  setEnableReviewOrderBtn(true);
                } else if (event.detail.message === PaymentEvents.NotExists) {
                  setEnableReviewOrderBtn(false);
                } else if (event.detail.message === PaymentEvents.Changed) {
                  setEnableReviewOrderBtn(false);
                }
              }
            );
          }
          window.Ucp?.events?.addEventListener(
            "event-guest-payment-method",
            (event: any) => {
              setPaymentEventMessage(event.detail.message);
              if (event.detail.message === PaymentEvents.Valid || event.detail.message === PaymentEvents.Saved) {
                setValidPayment(true);
              }
              switch (event.detail.message) {
                case PaymentEvents.Valid:
                  setSavePayment(false);
                  setEnableReviewOrderBtn(true);
                  break;
                case PaymentEvents.Invalid:
                case PaymentEvents.MissingInput:
                default:
                  setSavePayment(false);
                  setEnableReviewOrderBtn(false);
                  break;
              }
            }
          );

          let initEnvironment =
            originEnvironment === "DEV"
              ? `${originEnvironment}_UE1`
              : originEnvironment;

          const initOptions = {
            ...((!cartData?.idt || !isLoggedIn) && {
              guestSessionId: cartData?.paymentSession?.id,
            }),
            ...(cartData?.idt && isLoggedIn && { token: cartData?.idt }),
            environment: initEnvironment as EnvironmentAPI,
            theme: "light" as Theme,
            nonce: uuidv4(),
            locale: localeWith_,
            currency: defaultCurrency.toLowerCase(),
            clientFlavor: "public",

          };
          Ucp.initialize(initOptions);
        })
        .catch((error) => {
          console.error("Failed to initialize UCP:", error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartData, cartData?.paymentSession?.id, cartData?.idt, isLoggedIn]);

  // Handle form state updates based on payment event
  useEffect(() => {
    if (paymentEventMessage === PaymentEvents.Saved && formFieldsRef.current) {
      const { billing, shipping } = formFieldsRef.current;

      if (billing.address && billing.data) {
        setFormBillingAddress((prevState) => ({
          ...prevState,
          ...billing.address,
        }));

        setFormBillingData((prevState) => ({
          ...prevState,
          ...billing.data,
        }));
      }

      if (shipping.address && shipping.data) {
        setFormShippingAddress((prevState) => ({
          ...prevState,
          ...shipping.address,
        }));

        setFormData((prevState) => ({
          ...prevState,
          ...shipping.data,
        }));
      }

      submitCustomerForm();
      setPaymentEventMessage(null); // Reset the event message
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentEventMessage]);

  // Initialize custom hooks for extracted functionality
  const { handleSuccess } = useCartSuccess({
    currencySign,
    locale,
    countyField,
    userType,
    savedBillingAddress,
    savedShippingAddress,
    t,
    setBrand,
    setFormTaxData,
    setIsBusinessPurchase,
    setIsShipping,
    setCartData,
    setShippingList,
    setShippingOptions,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setFormBillingData,
    setFormBillingAddress,
    setSelectedBillingAddress,
    setIsBillingReadonly,
    setFormData,
    setSelectedShippingAddress,
    setIsShippingReadonly,
    formFieldsRef,
    getStateValue,
    emptyCart,
    orderAccount
  });

  const { updateSuggestedBillingAddress, updateSuggestedShippingAddress } = useAddressHandlers({
    setFormBillingAddress,
    setFormBillingData,
    setFormData,
    setFormShippingAddress,
    setSavedBillingAddress,
    setSavedShippingAddress,
    setSelectedBillingAddress,
    setSelectedShippingAddress,
    validateForm
  });

  // Note: handleDualAddressSuccess is defined below (line ~2340) and referenced here
  // We'll integrate useAddressModal after handleDualAddressSuccess is defined

  // Update ref when form values change
  useEffect(() => {
    if (formBillingAddress && formBillingData) {
      if (
        !isObjectEmpty(formBillingAddress) &&
        !isObjectEmpty(formBillingData)
      ) {
        formFieldsRef.current.billing = {
          address: formBillingAddress,
          data: formBillingData,
        };
      }
    }
  }, [formBillingAddress, formBillingData]);

  useEffect(() => {
    if (formShippingAddress && formData) {
      if (!isObjectEmpty(formShippingAddress) && !isObjectEmpty(formData)) {
        formFieldsRef.current.shipping = {
          address: formShippingAddress,
          data: formData,
        };
      }
    }
  }, [formShippingAddress, formData]);

  useEffect(() => {
    // This will be executed after the state has been updated
    let fieldName = fieldNameRef.current;
    if (fieldName) {
      validateForm(fieldName, formBillingData[fieldName], "billing");
    }
    // Ensure selectedBillingAddress is of type iSavedAddress before accessing properties
    if (selectedBillingAddress && selectedBillingAddress !== RESET_ADDRESS) {
      const address = selectedBillingAddress as intrfce.iSavedAddress;
      setFormData((prevData) => ({
        ...prevData,
        iBillingFormData: {
          ...(address.id ? { id: Number(address.id) } : {}),
          firstNameBilling: address.firstName,
          lastNameBilling: address.lastName,
          contactBilling: address.phoneNumber,
          emailBilling: formBillingData.emailBilling,
          vemailBilling: formBillingData.vemailBilling
        },
        iBillingFormAddress: {
          pincode: address.postalCode,
          address: address.line1,
          line2: address.line2 || "",
          cityValue: address.city,
          stateValue: getStateValue(address.countrySubdivision, '') || '',
          county: countyField ? address.county || "-" : '',
          countryValue: address.country,
          countryName: address.countryName,
        },
        iShippingDetails: {
          shippingOption: shippingOptions.shippingOption
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        firstNameShiping: isShipping
          ? formData.firstNameShiping
          : formBillingData.firstNameBilling,
        lastNameShiping: isShipping
          ? formData.lastNameShiping
          : formBillingData.lastNameBilling,
        contactShiping: isShipping
          ? formData.contactShiping
          : formBillingData.contactBilling,
        iBillingFormAddress: formBillingAddress,
        iBillingFormData: {
          firstNameBilling: formBillingData.firstNameBilling,
          lastNameBilling: formBillingData.lastNameBilling,
          contactBilling: formBillingData.contactBilling,
          ...(nvCookieHelpers.getCookie("isUserLoggedIn")
            ? { emailBilling: cartData?.emailAddress || "" }
            : { emailBilling: formBillingData.emailBilling || "" }),
          ...(nvCookieHelpers.getCookie("isUserLoggedIn")
            ? { vemailBilling: cartData?.emailAddress || "" }
            : { vemailBilling: formBillingData.emailBilling || "" }),
        },
        ...(!isShipping
          ? { iShippingFormAddress: formBillingAddress }
          : {
            iShippingFormAddress: {
              pincode: formShippingAddress.pincode,
              address: formShippingAddress.address,
              cityValue: formShippingAddress.cityValue,
              stateValue:
                locale === "en-us"
                  ? formShippingAddress.stateValue || "-"
                  : '',
              county:
                locale !== "en-us"
                  ? formShippingAddress.county || "-"
                  : '',
              countryValue: formShippingAddress.countryValue,
              countryName: formShippingAddress.countryName,
            },
          }),
        iShippingDetails: {
          shippingOption: shippingOptions["shippingOption"]
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateForm, isShipping]);

  useEffect(() => {
    // This will be executed after the state has been updated
    let fieldName = fieldNameRef.current;
    // Skip validation for companyName and vatNo here - they're handled separately
    if (fieldName && fieldName !== "companyName" && fieldName !== "vatNo") {
      validateForm(fieldName, formTaxData[fieldName], "billing");
    }
  }, [formTaxData, validateForm]);
  // Separate useEffect to handle interdependent validation of company name and VAT number
  useEffect(() => {
    if (!isBusinessPurchase) {
      // Clear errors if business purchase is unchecked
      setValidationErrors((prev) => ({
        ...prev,
        companyName: '',
        vatNo: '',
      }));
      return;
    }

    let companyNameError = '';
    let vatNoError = '';

    // If VAT number is filled, company name is required
    if (formTaxData.vatNo && formTaxData.vatNo.trim() !== '') {
      if (!formTaxData.companyName || formTaxData.companyName.trim() === '') {
        companyNameError = t("COMPANY_NAME_REQUIRED");
      }
    }

    // If company name is filled, VAT number is required
    if (formTaxData.companyName && formTaxData.companyName.trim() !== '') {
      if (!formTaxData.vatNo || formTaxData.vatNo.trim() === '') {
        vatNoError = t("VAT_NUMBER_REQUIRED");
      }
    }

    // Update validation errors
    setValidationErrors((prev) => ({
      ...prev,
      companyName: companyNameError,
      vatNo: vatNoError,
    }));
  }, [formTaxData.companyName, formTaxData.vatNo, isBusinessPurchase, t]);

  useEffect(() => {
    // Skip auto-loading addresses if we're in restricted shipping error state
    if (skipAddressAutoLoad.current) {
      console.log("Skipping address auto-load due to error state");
      return;
    }
    
    let isNewBillingAddressExists: intrfce.iSavedAddress | undefined;
    let isNewShippingAddressExists: intrfce.iSavedAddress | undefined;
    let isBillingAddressEmpty: boolean = false;
    let isShippingAddressEmpty: boolean = false;
    let updatedBillingAddresses: intrfce.iSavedAddress[] | undefined;
    let updatedShippingAddresses: intrfce.iSavedAddress[] | undefined;
    let shippingAddressArray: intrfce.iSavedAddress[];
    let billingAddressArray: intrfce.iSavedAddress[];
    let newBillingAddressId: any;
    let newShippingAddressId: any;
    let defaultBillingAddress: any;
    let defaultShippingAddress: any;
    let newShippingAddress: any;
    let newBillingAddress: any;
    console.log("selectedBillingAddress==>", selectedBillingAddress);
    if (typeof selectedBillingAddress !== "string") {
      isBillingAddressEmpty = isEmptyAddress(selectedBillingAddress, t("ADD_NEW_ADDRESS"));
    }
    if (typeof selectedShippingAddress !== "string") {
      isShippingAddressEmpty = isEmptyAddress(selectedShippingAddress, t("ADD_NEW_ADDRESS"));
    }

    if (
      savedBillingAddress &&
      savedBillingAddress.length &&
      savedShippingAddress &&
      savedShippingAddress.length
    ) {
      billingAddressArray = updatedBillingAddresses?.length
        ? updatedBillingAddresses
        : savedBillingAddress;
      shippingAddressArray = updatedShippingAddresses?.length
        ? updatedShippingAddresses
        : savedShippingAddress;

      isNewBillingAddressExists = billingAddressArray.find(
        (addr) => Number(addr.id) <= 0
      );
      isNewShippingAddressExists = shippingAddressArray.find(
        (addr) => Number(addr.id) <= 0
      );
      if (
        typeof selectedBillingAddress === "string" ||
        typeof selectedShippingAddress === "string"
      ) {
        // Handle billing address placeholder
        newBillingAddressId = handleNewAddressPlaceholder(
          selectedBillingAddress,
          savedBillingAddress,
          (addresses) => {
            setSavedBillingAddress(addresses);
            // If shipping is disabled, sync shipping addresses with billing
            if (!isShipping) {
              setSavedShippingAddress(addresses);
            }
          },
          setSelectedBillingAddress,
          addressOptions,
          t("ADD_NEW_ADDRESS")
        );

        // Handle shipping address placeholder if shipping is enabled
        if (isShipping) {
          newShippingAddressId = handleNewAddressPlaceholder(
            selectedShippingAddress,
            savedShippingAddress,
            setSavedShippingAddress,
            setSelectedShippingAddress,
            addressOptions,
            t("ADD_NEW_ADDRESS")
          );
        }
      }
      if (cartData &&
        (cartData.billingAddress?.id === -1 ||
          cartData.shippingAddress?.id === -1 ||
          cartData.billingAddress?.id === "0" ||
          cartData.shippingAddress?.id === "0")
      ) {
        if (isShipping) {
          if (
            !isNewShippingAddressExists &&
            !isShippingAddressEmpty &&
            !(Number(cartData.shippingAddress?.id) > 0)
          ) {
            newShippingAddress = createAddressFromCartData(
              cartData.shippingAddress,
              addressOptions,
              "Order Address"
            );
            updatedShippingAddresses = [
              newShippingAddress,
              ...savedShippingAddress,
            ];
            setSavedShippingAddress(updatedShippingAddresses);
            newShippingAddressId = newShippingAddress.id;
          } else {
            if (
              typeof selectedShippingAddress !== "string" &&
              selectedShippingAddress?.id !==
              Number(cartData.shippingAddress.id)
            ) {
              newShippingAddress = shippingAddressArray.find(
                (addr) => addr.id === Number(selectedShippingAddress?.id)
              );
            } else {
              newShippingAddress = shippingAddressArray.find(
                (addr) => addr.id === Number(cartData.shippingAddress?.id)
              );
            }
            if (newShippingAddress && !isShippingAddressEmpty) {
              newShippingAddressId = isShipping
                ? newShippingAddress.id
                : (typeof selectedBillingAddress !== "string" && (selectedBillingAddress as intrfce.iSavedAddress)?.id);
            } else {
              newShippingAddressId = "reset";
            }
          }
        }

        if (
          !isNewBillingAddressExists &&
          !isBillingAddressEmpty &&
          !(Number(cartData.billingAddress?.id) > 0)
        ) {
          newBillingAddress = createAddressFromCartData(
            cartData.billingAddress,
            addressOptions,
            "Order Address"
          );
          updatedBillingAddresses = [newBillingAddress, ...savedBillingAddress];
          setSavedBillingAddress(updatedBillingAddresses);
          if (!isShipping) {
            updatedShippingAddresses = [
              newBillingAddress,
              ...savedShippingAddress,
            ];
            setSavedShippingAddress(updatedShippingAddresses);
          }
          newBillingAddressId = newBillingAddress.id;
        } else {
          if (
            typeof selectedBillingAddress !== "string" &&
            selectedBillingAddress?.id !== Number(cartData.billingAddress.id)
          ) {
            newBillingAddress = billingAddressArray.find(
              (addr) => addr.id === Number(selectedBillingAddress?.id)
            );
          } else {
            newBillingAddress = billingAddressArray.find(
              (addr) => addr.id === Number(cartData.billingAddress?.id)
            );
          }
          if (newBillingAddress && !isBillingAddressEmpty) {
            newBillingAddressId = newBillingAddress.id;
          } else {
            newBillingAddressId = "reset";
          }
        }
      } else if (isBillingAddressEmpty || isShippingAddressEmpty) {
        console.log(selectedBillingAddress);
        if (isBillingAddressEmpty) {
          newBillingAddressId = "reset";
        } else {
          if (typeof selectedBillingAddress !== "string") {
            newBillingAddressId = selectedBillingAddress?.id;
          }
        }
        if (isShippingAddressEmpty) {
          newShippingAddressId = "reset";
        } else {
          if (typeof selectedShippingAddress !== "string") {
            newShippingAddressId = selectedShippingAddress?.id;
          }
        }
      } else if (
        typeof selectedBillingAddress !== "string" &&
        typeof selectedShippingAddress !== "string" &&
        selectedShippingAddress?.id !== undefined &&
        selectedShippingAddress?.id !== null &&
        selectedBillingAddress?.id !== undefined &&
        selectedBillingAddress?.id !== null
      ) {
        if (
          isShipping &&
          typeof selectedShippingAddress !== "string" &&
          selectedShippingAddress.id === formData?.id
        ) {
          isNewShippingAddressExists = shippingAddressArray.find(
            (addr) => Number(addr.id) === selectedShippingAddress?.id
          );
          newShippingAddressId = isNewShippingAddressExists?.id;
        } else {
          newShippingAddressId = selectedShippingAddress?.id;
        }
        if (
          typeof selectedBillingAddress !== "string" &&
          selectedBillingAddress.id === formBillingData?.id
        ) {
          isNewBillingAddressExists = billingAddressArray.find(
            (addr) => Number(addr.id) === selectedBillingAddress?.id
          );
          newBillingAddressId = isNewBillingAddressExists?.id;
        } else {
          newBillingAddressId = selectedBillingAddress?.id;
        }
      } else {
        isNewBillingAddressExists = billingAddressArray.find(
          (addr) => addr.id === Number(cartData?.billingAddress?.id)
        );
        if (!isNewBillingAddressExists) {
          isNewBillingAddressExists = billingAddressArray.find(
            (addr) => addr.id === -1
          );
          if (!isNewBillingAddressExists && selectedBillingAddress) {
            isNewBillingAddressExists = billingAddressArray.find(
              (addr: intrfce.iSavedAddress) => addr.id === Number((selectedBillingAddress as intrfce.iSavedAddress).id)
            );
          }
        }
        newBillingAddressId = isNewBillingAddressExists?.id;

        isNewShippingAddressExists = shippingAddressArray.find(
          (addr) => addr.id === Number(cartData?.shippingAddress?.id)
        );
        if (!isNewShippingAddressExists) {
          isNewShippingAddressExists = shippingAddressArray.find(
            (addr) => addr.id === -1
          );
          if (!isNewShippingAddressExists && selectedShippingAddress) {
            isNewShippingAddressExists = shippingAddressArray.find(
              (addr: intrfce.iSavedAddress) => addr.id === Number((selectedShippingAddress as intrfce.iSavedAddress).id)
            );
          }
        }
        newShippingAddressId = isNewShippingAddressExists?.id;
      }
    }

    // Billing Address Logic
    if (
      savedBillingAddress &&
      savedBillingAddress.length &&
      userType === "loggedIn"
    ) {
      defaultBillingAddress = savedBillingAddress.find(
        (addr) => addr.isDefault
      );
      const billingAddressIdToUse =
        newBillingAddressId !== undefined
          ? newBillingAddressId
          : defaultBillingAddress?.id;
      if (
        billingAddressIdToUse !== undefined &&
        billingAddressIdToUse !== "reset" &&
        typeof selectedBillingAddress !== "string"
      ) {
        handleSavedBillingAddressChange(
          "savedBillingAddress",
          billingAddressIdToUse,
          updatedBillingAddresses
        );
      }
    }

    // Shipping Address Logic
    if (
      savedShippingAddress &&
      savedShippingAddress.length &&
      userType === "loggedIn"
    ) {
      defaultShippingAddress = savedShippingAddress.find(
        (addr) => addr.isDefault
      );
      const shippingAddressIdToUse =
        newShippingAddressId !== undefined
          ? newShippingAddressId
          : defaultShippingAddress?.id;
      if (
        shippingAddressIdToUse !== undefined &&
        typeof selectedShippingAddress !== "string" &&
        shippingAddressIdToUse !== "reset"
      ) {
        handleSavedShippingAddressChange(
          "savedShippingAddress",
          shippingAddressIdToUse,
          updatedShippingAddresses
        );
      }
    }

    // If isShipping is false, use the same logic for billing and shipping
    if (
      !isShipping &&
      savedBillingAddress &&
      savedBillingAddress.length &&
      savedShippingAddress &&
      savedShippingAddress.length &&
      userType === "loggedIn"
    ) {
      const defaultAddress = savedBillingAddress.find((addr) => addr.isDefault);
      const addressIdToUse =
        newBillingAddressId !== undefined
          ? newBillingAddressId
          : defaultAddress?.id;

      if (
        addressIdToUse !== undefined &&
        addressIdToUse !== "reset" &&
        typeof selectedBillingAddress !== "string" &&
        selectedBillingAddress?.id !== addressIdToUse
      ) {
        handleSavedBillingAddressChange(
          "savedBillingAddress",
          addressIdToUse,
          updatedBillingAddresses
        );
        handleSavedShippingAddressChange(
          "savedShippingAddress",
          addressIdToUse,
          updatedShippingAddresses
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedBillingAddress,
    savedShippingAddress,
    selectedBillingAddress,
    selectedShippingAddress,
    isShipping,
    cartData,
  ]);

  const handleSavedShippingAddressChange = useCallback((
    fieldName: string,
    selectedValue: any,
    addrArr?: any
  ) => {
    console.log("handleSavedShippingAddressChange==>", selectedValue);
    
    // Clear shippingAddressErr when user manually selects a saved address (only if we're in error state)
    if (validationErrors.shippingAddressErr && hasRestrictedShippingError.current) {
      console.log("Clearing shippingAddressErr - user selected saved address");
      setValidationErrors((prevErrors) => {
        const { shippingAddressErr, ...restErrors } = prevErrors;
        return restErrors;
      });
      setEnableReviewOrderBtn(true);
      hasRestrictedShippingError.current = false;
    }
    
    if (selectedValue === "reset") {
      // Clear only shipping-related validation errors when adding new shipping address
      setValidationErrors((prevErrors) => {
        const {
          firstNameShiping,
          lastNameShiping,
          contactShiping,
          shippingaddress,
          addressShipping,
          cityShipping,
          stateNameShipping,
          zipShipping,
          countryShipping,
          shippingAddressErr,
          ...restErrors
        } = prevErrors;
        return restErrors;
      });

      setIsShippingReadonly(false);
      const emptyAddress = {
        pincode: "",
        address: "",
        line2: "",
        cityValue: "",
        stateValue: "",
        countryName: "",
        countryValue: "",
      };
      setFormShippingAddress(emptyAddress);
      setFormData((prevData) => {
        const { id, ...rest } = prevData;
        return {
          ...rest,
          firstNameShiping: "",
          lastNameShiping: "",
          contactShiping: "",
          iShippingFormAddress: emptyAddress,
        };
      });
      setSelectedShippingAddress(selectedValue);

      // Check for objects with id === -1 in savedShippingAddress and make their values blank
      if (savedShippingAddress && savedShippingAddress.length) {
        setSavedShippingAddress((prev) => {
          return prev.map((addr) => {
            if (addr.id === -1) {
              return createNewAddressPlaceholder(addressOptions, t("ADD_NEW_ADDRESS"));
            }
            return addr;
          });
        });
      }
    } else {
      const addresses = addrArr ? addrArr : savedShippingAddress;
      let selectedAddress = addresses?.filter(
        (ship) => ship.id === Number(selectedValue)
      );
      let updatedAddress;
      if (selectedAddress) {
        updatedAddress = selectedAddress[0];
        setSelectedShippingAddress(updatedAddress);
        if (updatedAddress) {
          const formAddress = savedAddressToFormAddress(updatedAddress, addressOptions);
          setFormShippingAddress(formAddress);
          setFormData((prevData) => ({
            ...prevData,
            ...(updatedAddress.id ? { id: Number(updatedAddress.id) } : {}),
            firstNameShiping: updatedAddress?.firstName || "",
            lastNameShiping: updatedAddress?.lastName || "",
            contactShiping: updatedAddress?.phoneNumber || "",
            iShippingFormAddress: formAddress,
          }));
        }
        if (Number(selectedValue) === 0 || Number(selectedValue) === -1) {
          setIsShippingReadonly(false);
        } else {
          setIsShippingReadonly(userType === "loggedIn");
        }
      }
    }
  }, [validationErrors.shippingAddressErr, savedShippingAddress, addressOptions, userType, t, setValidationErrors, setEnableReviewOrderBtn, setIsShippingReadonly, setFormShippingAddress, setFormData, setSelectedShippingAddress, setSavedShippingAddress]);

  const handleSavedBillingAddressChange = (
    fieldName: string,
    selectedValue: any,
    addrArr?: any
  ) => {
    console.log("handleSavedBillingAddressChange==>", selectedValue);
    if (selectedValue === "reset") {
      // Clear only billing-related validation errors when adding new billing address
      setValidationErrors((prevErrors) => {
        const {
          firstNameBilling,
          lastNameBilling,
          emailBilling,
          vemailBilling,
          contactBilling,
          billingaddress,
          addressBilling,
          cityBilling,
          stateNameBilling,
          zipBilling,
          countryBilling,
          addressErr,
          ...restErrors
        } = prevErrors;
        return restErrors;
      });
      setAddressErr("");

      setIsBillingReadonly(false);
      setFormBillingAddress(createEmptyFormAddress(countryList));
      setFormBillingData(createEmptyBillingData(cartData?.emailAddress));
      setSelectedBillingAddress(selectedValue);
      if (!isShipping) {
        handleSavedShippingAddressChange("savedShippingAddress", selectedValue);
      }
    } else {
      const addresses = addrArr ? addrArr : savedBillingAddress;
      if (selectedValue < 0) {
        setFormBillingData({
          ...(formBillingData.id ? { id: Number(formBillingData.id) } : {}),
          firstNameBilling: formBillingData?.firstNameBilling || "",
          lastNameBilling: formBillingData?.lastNameBilling || "",
          contactBilling: formBillingData?.contactBilling || "",
          emailBilling:
            cartData?.emailAddress || formBillingData?.emailBilling || "",
          vemailBilling: ""
        });

        if (!isShipping) {
          setFormShippingAddress(formBillingAddress);
          setFormData((prevData) => ({
            ...prevData,
            iShippingFormAddress: {
              ...formBillingAddress,
              firstNameShiping: formBillingData?.firstNameBilling || "",
              lastNameShiping: formBillingData?.lastNameBilling || "",
              contactShiping: formBillingData?.contactBilling || "",
            },
          }));
        }
      } else {
        const selectedAddress = addresses?.filter(
          (bill) => bill.id === Number(selectedValue)
        );
        if (selectedAddress) {
          let updatedAddress = selectedAddress[0];
          setSelectedBillingAddress(updatedAddress);
          console.log("handleSavedBillingAddressChange==>", fieldName);

          if (updatedAddress) {
            const formAddress = savedAddressToFormAddress(updatedAddress, addressOptions);
            setFormBillingAddress(formAddress);

            const billingData = {
              ...(updatedAddress.id ? { id: Number(updatedAddress.id) } : {}),
              firstNameBilling: updatedAddress?.firstName || "",
              lastNameBilling: updatedAddress?.lastName || "",
              contactBilling: updatedAddress?.phoneNumber || "",
              emailBilling: cartData?.emailAddress || "",
              vemailBilling: ""
            };

            setFormBillingData(billingData);
            setFormData((prevData) => ({
              ...prevData,
              iBillingFormData: billingData,
              iBillingFormAddress: formAddress,
            }));

            if (Number(selectedValue) === 0 || Number(selectedValue) === -1) {
              setIsBillingReadonly(false);
            } else {
              setIsBillingReadonly(userType === "loggedIn");
            }

            // Validate all billing fields
            Object.keys(formBillingData).forEach((fieldName) => {
              if (
                formBillingData[fieldName] &&
                formBillingData[fieldName].length
              ) {
                validateForm(fieldName, formBillingData[fieldName], "billing");
              }
            });
            Object.keys(formBillingAddress).forEach((fieldName) => {
              if (
                formBillingAddress[fieldName] &&
                formBillingAddress[fieldName].length
              ) {
                validateForm(
                  fieldName,
                  formBillingAddress[fieldName],
                  "billing"
                );
              }
            });
          }
        }
      }
    }
  };
  const handleInputChange = useCallback((fieldName: string, value: string) => {
    fieldNameRef.current = fieldName;
    setFormData((prevState) => ({ ...prevState, [fieldName]: value }));

    // Only validate non-address fields on change
    // Address fields will be validated on blur
    const addressFields = ['addressShipping', 'addressBilling', 'address'];
    if (!addressFields.includes(fieldName)) {
      validateForm(fieldName, value, "shipping");
    }
    
    // Clear shippingAddressErr when user manually changes any shipping field
    if (validationErrors.shippingAddressErr && hasRestrictedShippingError.current && (
      fieldName === "firstNameShiping" || 
      fieldName === "lastNameShiping" || 
      fieldName === "contactShiping"
    )) {
      console.log("Clearing shippingAddressErr - user manually changed field:", fieldName);
      setValidationErrors((prevErrors) => {
        const { shippingAddressErr, ...restErrors } = prevErrors;
        return restErrors;
      });
      setEnableReviewOrderBtn(true);
      hasRestrictedShippingError.current = false;
    }
  }, [validateForm, validationErrors.shippingAddressErr, setFormData, setValidationErrors, setEnableReviewOrderBtn]);

  // Handler for address field blur - validates only on blur
  const handleAddressBlur = useCallback((fieldName: string, value: string, formType: "billing" | "shipping" | "") => {
    validateForm(fieldName, value, formType);
  }, [validateForm]);

  const shipingInfo = `shipingInfo ${isShipping ? " visible" : ""}`;
  useEffect(() => {
    // This will be executed after the state has been updated
    // Skip validation for address fields as they are validated on blur
    let fieldName = fieldNameRef.current;
    const addressFields = ['addressShipping', 'addressBilling', 'address'];

    if (fieldName && !addressFields.includes(fieldName)) {
      validateForm(fieldName, formData[fieldName], "shipping");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);
  const handleCartChange = useCallback(async (
    productId?: string,
    selectedOption?: number,
    shippingOption?: string,
    coupon?: string,
    couponApplied?: boolean
  ) => {
    showCartLoader();
    if (productId && selectedOption !== undefined && selectedOption !== null) {
      await updateCart(
        handleSuccess,
        handleError,
        productId,
        selectedOption,
        shippingOption,
        undefined,
        undefined,
        false
      );
    } else if (shippingOption !== undefined && shippingOption !== null) {
      await updateCart(
        handleSuccess,
        handleError,
        undefined,
        undefined,
        shippingOption,
        undefined,
        undefined,
        false
      );
    } else {
      if (coupon) {
        await updateCart(
          handleSuccess,
          handleCouponError,
          undefined,
          undefined,
          undefined,
          coupon,
          undefined,
          false
        );
      } else {
        if (couponApplied) {
          await updateCart(
            handleSuccess,
            handleCouponError,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            couponApplied
          );
        }
      }
    }
    hideCartLoader();
  }, [showCartLoader, updateCart, handleSuccess, handleError, handleCouponError, hideCartLoader]);

  const handleCouponChange = useCallback((fieldName: string, promo: string | null) => {
    fieldNameRef.current = fieldName;
    setCoupon(promo);
    if (promo || promo?.length === 0) {
      setPromoErr(false);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        promoCode: "",
      }));
    }
    console.log("promo==>", promo);
  }, []);

  const handleCouponDelete = useCallback((d: boolean) => {
    handleCartChange(undefined, undefined, undefined, undefined, d);
  }, [handleCartChange]);

  const handleCouponSubmit = useCallback((c: string) => {
    handleCartChange(undefined, undefined, undefined, c);
  }, [handleCartChange]);

  const handleisShippingChange = useCallback((
    e?: any,
    shippingState?: boolean
  ) => {
    setIsShipping(shippingState ?? !isShipping);
    sessionStorage.setItem("isShipping", JSON.stringify(!isShipping));
    setFormShippingAddress(formBillingAddress);
    if (
      isShipping &&
      savedShippingAddress &&
      savedShippingAddress.length &&
      !selectedShippingAddress &&
      userType === "loggedIn"
    ) {
      // Find the default address if available
      const defaultAddress =
        formBillingData && formBillingData.id
          ? formBillingData
          : savedShippingAddress.find((addr) => addr.isDefault);
      if (defaultAddress) {
        handleSavedShippingAddressChange(
          "savedShippingAddress",
          JSON.stringify(defaultAddress.id)
        );
      }
    } else {
      // When unchecking (billing different from shipping becomes false), 
      // just copy billing to shipping without modifying savedShippingAddress array
      let billingAddressCopy = {
        city: formBillingAddress.cityValue || "",
        countryName: formBillingAddress.countryName,
        country: formBillingAddress.countryValue,
        firstName: formBillingData.firstNameBilling,
        lastName: formBillingData.lastNameBilling,
        line1: formBillingAddress.address || "",
        line2: formBillingAddress.line2 || "",
        nickName: selectedBillingAddress && selectedBillingAddress !== "reset"
          ? selectedBillingAddress.nickName
          : "",
        phoneNumber: formBillingData.contactBilling,
        ...(formBillingData.id ? { id: Number(formBillingData.id) } : {}),
        ...(selectedBillingAddress !== "reset"
          ? { id: Number(selectedBillingAddress?.id) }
          : {}),
        postalCode: formBillingAddress.pincode || "",
        countrySubdivision: formBillingAddress.stateValue || "",
      };

      // Only set the selected address, don't modify the savedShippingAddress array
      setSelectedShippingAddress(billingAddressCopy);
    }
    setFormData((prevData) => ({
      ...prevData,
      ...(selectedBillingAddress !== "reset"
        ? { id: Number(selectedBillingAddress?.id) }
        : {}),
      firstNameShiping: formBillingData.firstNameBilling,
      lastNameShiping: formBillingData.lastNameBilling,
      contactShiping: formBillingData.contactBilling,
      iShippingFormAddress: {
        ...prevData.iShippingFormAddress,
        pincode: formBillingAddress.pincode,
        address: formBillingAddress.address,
        cityValue: formBillingAddress.cityValue,
        line2: formBillingAddress.line2 || "",
        stateValue: formBillingAddress.stateValue || "-",
        countryName: formBillingAddress.countryName,
        countryValue: formBillingAddress.countryValue,
      },
    }));
  }, [isShipping, formBillingAddress, formBillingData, savedShippingAddress, selectedShippingAddress, userType, selectedBillingAddress, setIsShipping, setFormShippingAddress, handleSavedShippingAddressChange, setSelectedShippingAddress, setFormData]);

  // Cart initialization logic extracted to custom hook
  useCartInitialization({
    orderState,
    cartData,
    stateDropdown,
    locale,
    countyField,
    formBillingAddress,
    t,
    fetchCart,
    emptyCart,
    hideBodyLoader,
    showBodyLoader,
    handleSavedBillingAddressChange,
    setFormBillingData,
    setFormBillingAddress,
    setFormShippingAddress,
    setSelectedBillingAddress,
    setFormData,
    setSelectedShippingAddress,
    setFormTaxData,
  });

  const handleisBusinessPurchaseChange = useCallback((e: ChangeEvent<HTMLElement> | React.MouseEvent<HTMLButtonElement>) => {
    setIsBusinessPurchase(!isBusinessPurchase);
    setFormTaxData({
      companyName: '',
      vatNo: '',
    });
  }, [isBusinessPurchase]);
  const billaddr = useMemo(() => createBillingAddressObject(
    formBillingData,
    formBillingAddress,
    addressOptions,
    cartData?.emailAddress ?? null
  ), [formBillingData, formBillingAddress, addressOptions, cartData?.emailAddress]);

  const shipaddr = useMemo(() => createShippingAddressObject(
    isShipping,
    formData,
    formBillingData,
    formShippingAddress,
    formBillingAddress,
    addressOptions,
    cartData?.emailAddress ?? null
  ), [isShipping, formData, formBillingData, formShippingAddress, formBillingAddress, addressOptions, cartData?.emailAddress]);

  //const shipopt = shippingOptions?.shippingOption?.toString();
  // Get the translated shipping method description from shippingList
  const shippingMethodDescription = useMemo(() => 
    shippingList && shippingList.length > 0
      ? shippingList[0].description
      : undefined,
    [shippingList]
  );

  const taxExemptObj = useMemo(() => ({
    companyName: formTaxData.companyName,
    vatNo: formTaxData.vatNo,
  }), [formTaxData.companyName, formTaxData.vatNo]);
  // useEffect(() => {
  //   isBillingAddressValid();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [formBillingAddress]);
  // useEffect(() => {
  //   isShippingAddressValid();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [formShippingAddress]);

  useEffect(() => {
    setOrder((prevData) => ({
      ...prevData,
      cart: {
        futureUse: true,
        billingAddress: billaddr,
        shippingAddress: shipaddr,
        shippingMethod: shippingMethodDescription,
        taxExemption: taxExemptObj,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formBillingData,
    formBillingAddress,
    formData,
    formShippingAddress,
    shippingOptions,
    formTaxData,
    shippingMethodDescription,
  ]);

  const handleShippingMethodChange = useCallback((fieldName: string, value: string) => {
    // Use the callback function to ensure the state is updated before performing other actions

    fieldNameRef.current = fieldName;
    setShippingOptions((prevShippingOptions) => {
      const newShippingOptions = { ...prevShippingOptions, [fieldName]: value };
      return newShippingOptions;
    });

    if (fieldName === "shippingOption") {
      handleCartChange(undefined, undefined, value);
    }
  }, [handleCartChange]);

  const handleCartQuantityChange = useCallback(async (
    productId: number,
    selectedOption: number
  ) => {
    let pid = productId.toString();
    handleCartChange(pid, selectedOption);
  }, [handleCartChange]);

  const addressRef = useRef<{
    formType: "billing" | "shipping" | null;
    data: intrfce.iAddressFormData | null;
  }>({
    formType: null,
    data: null,
  });

  // Handle user-initiated address changes from Places component
  const handleUserShippingAddressChange = useCallback(() => {
    if (validationErrors.shippingAddressErr && hasRestrictedShippingError.current) {
      console.log("Clearing shippingAddressErr - user changed address in Places component");
      setValidationErrors((prevErrors) => {
        const { shippingAddressErr, ...restErrors } = prevErrors;
        return restErrors;
      });
      setEnableReviewOrderBtn(true);
      hasRestrictedShippingError.current = false;
    }
  }, [validationErrors.shippingAddressErr]);

  const handleAddressUpdate = useCallback((data: any, formType: "billing" | "shipping") => {
    console.log("handleAddressUpdate===>", data, formType);
    let shipping: any = cartData?.shippingAddress;

    addressRef.current = { formType, data };

    if (formType === "shipping") {
      // Don't clear error here - this callback triggers on ANY change including programmatic updates
      // Error clearing is handled in user-initiated events only (handleInputChange, handleSavedShippingAddressChange)

      // Check if this is an empty address update during restricted error state
      const isEmptyAddress = !data.address && !data.pincode && !data.cityValue;
      if (hasRestrictedShippingError.current && isEmptyAddress) {
        console.log("Empty address during error - using billing address data instead");
        // Instead of ignoring, use the billing address data we already set
        const billingAddressData = addressRef.current.data || formBillingAddress;
        if (billingAddressData && (billingAddressData.address || billingAddressData.pincode)) {
          // Don't overwrite - keep the billing address that was already set
          return;
        }
      }

      if (isShipping) {
        setFormShippingAddress(data);
        if (selectedAddressOption !== "recommendedAddress") {
          setTemporaryAddress({
            line1: data.address,
            line2: data.line2,
            city: data.cityValue,
            countrySubdivision: data.stateValue || "",
            postalCode: data.pincode,
            countryName: data.countryName,
            country: data.countryValue,
            firstName: formData.firstNameShiping || "",
            lastName: formData.lastNameShiping || "",
            phoneNumber: formData.contactShiping || "",
          });
        }
        setFormData((prevData) => ({
          ...prevData,
          iShippingFormAddress: data,
        }));
      } else {
        if (
          typeof selectedShippingAddress !== "string" &&
          Number(cartData?.shippingAddress?.id) !== selectedShippingAddress?.id
        ) {
          shipping = selectedShippingAddress ?? undefined;
        }

        if (
          shipping &&
          shipping.firstName &&
          shipping.lastName &&
          shipping.phoneNumber &&
          shipping.postalCode &&
          shipping.line1 &&
          shipping.city &&
          shipping.countrySubdivision &&
          shipping.country
        ) {
          setFormData((prevData) => ({
            ...prevData,
            ...(shipping.id ? { id: Number(shipping.id) } : {}),
            firstNameShiping: shipping.firstName,
            lastNameShiping: shipping.lastName,
            contactShiping: shipping.phoneNumber,
            iShippingFormAddress: {
              pincode: shipping.postalCode,
              address: shipping.line1,
              cityValue: shipping.city,
              stateValue: getStateValue(shipping.countrySubdivision, '') || '',
              county: countyField ? shipping.county || "-" : '',
              countryValue: shipping.country,
              countryName: shipping.countryName,
            },
          }));
        } else {
          setFormShippingAddress(data);
          setFormData((prevData) => ({
            ...prevData,
            iShippingFormAddress: data,
          }));
        }
      }
    } else {
      setFormBillingAddress(data);
      if (!isShipping) {
        setFormShippingAddress(data);
      }
      setFormData((prevData) => ({
        ...prevData,
        ...(shipping && shipping.firstName ? { firstNameShiping: shipping?.firstName } : {}),
        ...(shipping && shipping.lastName ? { lastNameShiping: shipping?.lastName } : {}),
        ...(shipping && shipping.phoneNumber ? { contactShiping: shipping?.phoneNumber } : {}),
        iBillingFormAddress: data,
        ...(!isShipping
          ? { iShippingFormAddress: data }
          : {
            iShippingFormAddress: {
              pincode: shipping?.postalCode ?? undefined,
              address: shipping?.line1 ?? undefined,
              cityValue: shipping?.city ?? undefined,
              stateValue: getStateValue(shipping?.countrySubdivision, ''),
              county:
                countyField ? shipping?.county ?? "-" : '',
              countryValue: shipping?.country ?? undefined,
              countryName: shipping?.countryName ?? undefined,
            },
          }),
      }));
    }
  }, [cartData, formBillingAddress, isShipping, selectedAddressOption, formData, selectedShippingAddress, countyField, getStateValue, setFormShippingAddress, setTemporaryAddress, setFormData, setFormBillingAddress]);

  const handleCartSuccess = useCallback((res) => {
    if (res?.paymentSession?.id) {
      sessionStorage.setItem("paymentSession", res?.paymentSession?.id);
    }
    setTemporaryAddress(null);
    if (userType) {
      showBodyLoader();
      reviewOrder(userType);      
    }
  }, [userType, showBodyLoader, reviewOrder, setTemporaryAddress]);

  const handleCartError = useCallback((error: any) => {
    hideBodyLoader();

    // Navigate back to Step 1 for any error
    setCurrentStep(1);
    sessionStorage.removeItem('proceedToStep2');

    // Handle error object
    if (error && error.code === "postal_code_invalid") {
      console.error("Postal code invalid:", error.message);
      // Add custom logic for invalid postal code error here (e.g., show specific UI message)
      setAddressErr(error?.description); // Example usage
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        addressErr: error?.description,
      }));
    } else if (error && error.code === 'restricted-ship-to-country') {
      console.log('🚨 Restricted ship-to-country error:', error);
      setShowAddressModal(false);
      
      // Set error state refs to prevent premature clearing
      hasRestrictedShippingError.current = true;
      skipAddressAutoLoad.current = true;
      
      setEnableReviewOrderBtn(false);
      setValidationErrors((prevErrors) => ({
        ...prevErrors,
        shippingAddressErr: t('ADDRESS_NOT_SHIPPABLE'),
      }));

      // Ensure shipping section is visible with the address that caused the error
      // ONLY set ADD_NEW_ADDRESS when restricted-ship-to-country error occurs
      if (!isShipping) {
        setIsShipping(true);
        sessionStorage.setItem("isShipping", JSON.stringify(true));
        
        // Copy billing address to shipping fields AND to Places-compatible format
        setFormShippingAddress(formBillingAddress);
        setFormData((prevData) => ({
          ...prevData,
          firstNameShiping: formBillingData.firstNameBilling,
          lastNameShiping: formBillingData.lastNameBilling,
          contactShiping: formBillingData.contactBilling,
          iShippingFormAddress: formBillingAddress,
        }));
        
        // Also update addressRef so Places component knows the data exists
        addressRef.current = {
          formType: "shipping",
          data: formBillingAddress
        };
      } else {
        // If shipping is already enabled, keep current shipping address
        console.log("Shipping already enabled, keeping current shipping address for error display");
        
        // Update addressRef with current shipping address
        addressRef.current = {
          formType: "shipping",
          data: formShippingAddress
        };
      }
      
      // Set selectedShippingAddress to "ADD_NEW_ADDRESS" (-1) to prevent default address from loading
      // This happens ONLY for restricted-ship-to-country error
      // Always populate with current billing/shipping address data
      if (userType === "loggedIn") {
        const currentShippingAddress = !isShipping ? formBillingAddress : formShippingAddress;
        
        const addNewAddressWithData: intrfce.iSavedAddress = {
          id: -1,
          city: currentShippingAddress.cityValue || "",
          country: currentShippingAddress.countryValue || "",
          countryName: currentShippingAddress.countryName || "",
          countrySubdivision: getStateValue(currentShippingAddress.stateValue, "") || "",
          county: countyField ? (currentShippingAddress.county || "") : undefined,
          firstName: !isShipping 
            ? formBillingData.firstNameBilling || ""
            : formData.firstNameShiping || "",
          isDefault: false,
          lastName: !isShipping 
            ? formBillingData.lastNameBilling || ""
            : formData.lastNameShiping || "",
          line1: currentShippingAddress.address || "",
          line2: currentShippingAddress.line2 || "",
          nickName: t("ADD_NEW_ADDRESS"),
          phoneNumber: !isShipping 
            ? formBillingData.contactBilling || ""
            : formData.contactShiping || "",
          postalCode: currentShippingAddress.pincode || "",
        };
        
        console.log("Setting selectedShippingAddress with billing data:", addNewAddressWithData);
        setSelectedShippingAddress(addNewAddressWithData);
      }
      
      // Ensure shipping form is editable
      setIsShippingReadonly(false);
      
      // Reset skip flag after state updates complete (longer timeout to ensure all state updates finish)
      setTimeout(() => {
        skipAddressAutoLoad.current = false;
      }, 500);
    } else if (error && error.message) {
      // Handle other errors
      emptyCart(error.message);
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideBodyLoader, emptyCart, t, isShipping, formBillingAddress, formShippingAddress, formBillingData, formData, userType, stateDropdown, locale, countyField, setIsShipping, setFormShippingAddress, setFormData, setSelectedShippingAddress, setIsShippingReadonly, setShowAddressModal, setEnableReviewOrderBtn, setValidationErrors, setAddressErr, setCurrentStep]);

  // Update saved address arrays with actual form data for addresses with id === -1
  const updateSavedAddressesBeforeSubmit = useCallback((billaddr: any, shipaddr: any) => {
    // Update savedBillingAddress if it has an object with id === -1
    if (savedBillingAddress && savedBillingAddress.length > 0) {
      const hasNewBillingAddress = savedBillingAddress.some(addr => addr.id === -1);
      if (hasNewBillingAddress) {
        setSavedBillingAddress(updateAddressWithData(
          savedBillingAddress,
          billaddr,
          t("ADD_NEW_ADDRESS")
        ));
      }
    }

    // Update savedShippingAddress if it has an object with id === -1
    if (savedShippingAddress && savedShippingAddress.length > 0 && isShipping) {
      const hasNewShippingAddress = savedShippingAddress.some(addr => addr.id === -1);
      if (hasNewShippingAddress) {
        setSavedShippingAddress(updateAddressWithData(
          savedShippingAddress,
          shipaddr,
          t("ADD_NEW_ADDRESS")
        ));
      }
    }
  }, [savedBillingAddress, savedShippingAddress, isShipping, t, setSavedBillingAddress, setSavedShippingAddress]);

  const handleSubmitCart = useCallback(async (orderDet) => {
    let user: string | null = sessionStorage.getItem("customer");
    if (user && orderDet) {
      sessionStorage.setItem(user, JSON.stringify(orderDet));
      sessionStorage.setItem("isShipping", JSON.stringify(isShipping));
    }
    console.log("orderDet==>", orderDet);
    showBodyLoader();
    await submitCart(
        { ...orderDet, locale: locale, brand: brand },
        handleCartSuccess,
        handleCartError
    );
  }, [isShipping, showBodyLoader, submitCart, locale, brand, handleCartSuccess, handleCartError]);

  const submitCustomerForm = useCallback(() => {
    if (isBillingFormValid() && isBillingAddressValid() && isTaxDataValid()) {
      if (isShipping) {
        if (!isShippingFormValid() || !isShippingAddressValid()) {
          console.log(isShipping, formShippingAddress);
          return false; // Exit the function immediately if any field is invalid
        }
      }
      if (order && order?.cart && Object.keys(order?.cart).length === 0) {
        const billaddr = createBillingAddressObject(
          formBillingData,
          formBillingAddress,
          addressOptions,
          cartData?.emailAddress ?? null
        );
        const shipaddr = createShippingAddressObject(
          isShipping,
          formData,
          formBillingData,
          formShippingAddress,
          formBillingAddress,
          addressOptions,
          cartData?.emailAddress ?? null
        );
        //const shipopt = shippingOptions?.shippingOption?.toString();
        const taxExempt = {
          companyName: formTaxData.companyName,
          vatNo: formTaxData.vatNo
        };
        const updatedBillingAddress = billaddr; // Default to cartData's billingAddress
        const updatedShippingAddress = shipaddr; // Default to cartData's billingAddress
        // If cartData.billingAddress.emailAddress is null or empty, use cartData.emailAddress
        if (updatedBillingAddress && !updatedBillingAddress?.emailAddress) {
          updatedBillingAddress.emailAddress =
            cartData?.emailAddress || updatedBillingAddress.emailAddress;
        }
        if (updatedShippingAddress && !updatedShippingAddress?.emailAddress) {
          updatedShippingAddress.emailAddress =
            cartData?.emailAddress || updatedShippingAddress.emailAddress;
        }
        let cartPayload = {
          cart: {
            futureUse: futureUse,
            billingAddress: updatedBillingAddress,
            shippingAddress: updatedShippingAddress,
            shippingMethod: shippingMethodDescription,
            taxExemption: taxExempt,
          },
        };
        if (userType === "guest") {
          // Update saved addresses before submitting
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(cartPayload);
        } else {
          // Update saved addresses before submitting
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(cartPayload);
        }
      } else {
        if (userType === "guest") {
          // Update saved addresses before submitting (using billaddr and shipaddr from the global scope)
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(order);
        } else {
          // Update saved addresses before submitting (using billaddr and shipaddr from the global scope)
          updateSavedAddressesBeforeSubmit(billaddr, shipaddr);
          handleSubmitCart(order);
        }
      }
    } else {
      console.log("Form has found missing field values");
      return false;
    }
  }, [isBillingFormValid, isBillingAddressValid, isTaxDataValid, isShipping, isShippingFormValid, isShippingAddressValid, formShippingAddress, order, formBillingData, formBillingAddress, addressOptions, cartData?.emailAddress, formData, formTaxData, futureUse, shippingMethodDescription, userType, billaddr, shipaddr, updateSavedAddressesBeforeSubmit, handleSubmitCart]);
  
  // updateSuggestedBillingAddress and updateSuggestedShippingAddress are now provided by useAddressHandlers hook
  // handleConfirmAddress is now provided by useAddressModal hook (removed 263 lines)

  // Generic function to update recommended address for both billing and shipping
  const updateRecommendedAddress = useCallback((
    result: any,
    formAddress: intrfce.iAddressFormData,
    addressType: "shipping" | "billing",
    setter: (data: intrfce.iAddressFormData | null) => void,
    differencesSetter: (diffs: string[]) => void
  ) => {
    console.log(`updateRecommendedAddress - ${addressType}:`, result);

    if (result?.result?.address?.postalAddress) {
      const postalAddress = result.result.address.postalAddress;
      const addressComponents = result.result.address.addressComponents || [];

      // Extract address lines
      console.log(`[${addressType}] Google addressLines:`, postalAddress.addressLines);
      let line1 = '';
      let line2 = '';

      if (postalAddress.addressLines && postalAddress.addressLines.length > 0) {
        line1 = postalAddress.addressLines[0] || '';
        line2 = postalAddress.addressLines.length > 1 ? postalAddress.addressLines[1] || '' : '';

        // If there's no line2 but there's a subpremise component, extract it for line2
        if (!line2 && addressComponents.length > 0) {
          const subpremiseComponent = addressComponents.find((c: any) => c.componentType === 'subpremise');
          if (subpremiseComponent) {
            const subpremiseText = subpremiseComponent.componentName?.text;
            if (subpremiseText) {
              // Remove the subpremise from line1 (it might be at the end with #, -, etc.)
              const patterns = [
                new RegExp(`\\s*#${subpremiseText}$`, 'i'),
                new RegExp(`\\s*-${subpremiseText}$`, 'i'),
                new RegExp(`\\s*${subpremiseText}$`, 'i'),
                new RegExp(`\\s*#\\s*${subpremiseText}$`, 'i'),
              ];

              for (const pattern of patterns) {
                if (pattern.test(line1)) {
                  line1 = line1.replace(pattern, '').trim();
                  line2 = `#${subpremiseText}`;
                  break;
                }
              }
            }
          }
        }

        console.log(`[${addressType}] Extracted - line1: "${line1}", line2: "${line2}"`);
      }

      // Get country name from regionCode using the countryList
      const regionCode = postalAddress.regionCode || "US";
      const countryInfo = countryList.find(c => c.ISO_CC === regionCode);
      const countryName = countryInfo?.countryName || formAddress.countryName || '';

      const addressData: any = {
        pincode: postalAddress.postalCode || '',
        address: line1,
        line2: line2,
        cityValue: postalAddress.locality || '',
        stateValue: postalAddress.administrativeArea || '',
        countryName: countryName,
        countryValue: regionCode,
      };

      console.log(`${addressType} - Google addressData:`, addressData);
      console.log(`${addressType} - Form address:`, formAddress);

      // Check if addressData matches the form address (normalize empty strings vs undefined)
      const normalize = (val: any) => val || '';
      const differences: string[] = [];

      if (normalize(addressData.pincode) !== normalize(formAddress.pincode))
        differences.push("pincode");
      if (normalize(addressData.address) !== normalize(formAddress.address))
        differences.push("address");
      if (normalize(addressData.line2) !== normalize(formAddress.line2))
        differences.push("line2");
      if (normalize(addressData.cityValue) !== normalize(formAddress.cityValue))
        differences.push("cityValue");
      if (normalize(addressData.stateValue) !== normalize(formAddress.stateValue))
        differences.push("stateValue");
      if (normalize(addressData.countryName) !== normalize(formAddress.countryName))
        differences.push("countryName");
      if (normalize(addressData.countryValue) !== normalize(formAddress.countryValue))
        differences.push("countryValue");

      console.log(`${addressType} - Differences:`, differences);

      // Store differences for highlighting in modal
      differencesSetter(differences);

      // Validate each field
      Object.keys(addressData).forEach((fieldName) => {
        if (addressData[fieldName] && addressData[fieldName].length) {
          validateForm(fieldName, addressData[fieldName], addressType);
        }
      });

      const isEqual = differences.length === 0;
      console.log(`${addressType} - isEqual:`, isEqual, 'Setting recommended address:', isEqual ? null : addressData);
      setter(isEqual ? null : addressData);
    } else {
      console.log(`${addressType} - No postalAddress in result, setting null`);
      setter(null);
      differencesSetter([]);
    }
  }, [validateForm, countryList]);

  // Wrapper functions for backward compatibility
  const updateRecommendedShippingAddress = useCallback((result: any) => {
    updateRecommendedAddress(result, formShippingAddress, "shipping", setRecommendedShippingAddress, setShippingAddressDifferences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateRecommendedAddress, formShippingAddress]);

  const updateRecommendedBillingAddress = useCallback((result: any) => {
    updateRecommendedAddress(result, formBillingAddress, "billing", setRecommendedBillingAddress, setBillingAddressDifferences);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateRecommendedAddress, formBillingAddress]);

  // Handler for dual address validation (billing + shipping)
  const handleDualAddressSuccess = useCallback((billingResult: any, shippingResult: any) => {
    console.log("handleDualAddressSuccess==> Billing:", billingResult, "Shipping:", shippingResult);
    
    if (isShipping) {
      // Dual address mode: process both billing and shipping
      
      // Process billing address
      setBillingValidationResult(billingResult);
      updateRecommendedBillingAddress(billingResult);
      setTemporaryBillingAddress({
        id: 0,
        city: formBillingAddress.cityValue || "",
        country: formBillingAddress.countryValue || "US",
        countryName: formBillingAddress.countryName || "United States",
        countrySubdivision: formBillingAddress.stateValue || "",
        firstName: formBillingData.firstNameBilling || "",
        lastName: formBillingData.lastNameBilling || "",
        line1: formBillingAddress.address || "",
        line2: formBillingAddress.line2 || "",
        nickName: "Billing Address",
        phoneNumber: formBillingData.contactBilling || "",
        postalCode: formBillingAddress.pincode || "",
        isDefault: false,
      });
      
      // Process shipping address
      setShippingValidationResult(shippingResult);
      updateRecommendedShippingAddress(shippingResult);
      setTemporaryAddress(createTemporaryAddress(formData, formShippingAddress, "Shipping Address"));
    } else {
      // Single address mode: billing only (shipping is same as billing)
      // Put billing data into the shipping fields for the modal to display
      setAddressValidationResult(billingResult);
      setBillingValidationResult(billingResult);
      // IMPORTANT: Compare billing result against billing form address, not shipping!
      updateRecommendedAddress(billingResult, formBillingAddress, "billing", setRecommendedShippingAddress, setShippingAddressDifferences);
      setTemporaryAddress({
        id: 0,
        city: formBillingAddress.cityValue || "",
        country: formBillingAddress.countryValue || "US",
        countryName: formBillingAddress.countryName || "United States",
        countrySubdivision: formBillingAddress.stateValue || "",
        firstName: formBillingData.firstNameBilling || "",
        lastName: formBillingData.lastNameBilling || "",
        line1: formBillingAddress.address || "",
        line2: formBillingAddress.line2 || "",
        nickName: "Billing Address",
        phoneNumber: formBillingData.contactBilling || "",
        postalCode: formBillingAddress.pincode || "",
        isDefault: false,
      });
    }
    
    // Reset radio button selections to "yourAddress" when opening modal
    setSelectedAddressOption("yourAddress");
    setSelectedBillingAddressOption("yourAddress");

    setShowAddressModal(true);
    hideBodyLoader();
  }, [isShipping, formBillingAddress, formBillingData, formData, formShippingAddress, updateRecommendedBillingAddress, updateRecommendedShippingAddress, updateRecommendedAddress, setRecommendedShippingAddress, hideBodyLoader, setTemporaryAddress, setSelectedAddressOption, setSelectedBillingAddressOption]);

  // Integrate useAddressModal hook - provides address modal handlers
  const {
    handleConfirmAddress,
    handleShippingAddressUpdate,
    handleAddressOptionChange,
    handleContinueToPayment,
    handleBackToAddresses
  } = useAddressModal({
    isShipping,
    currentStep,
    formBillingData,
    formBillingAddress,
    formData,
    formShippingAddress,
    recommendedBillingAddress,
    recommendedShippingAddress,
    selectedBillingAddressOption,
    selectedAddressOption,
    locale,
    isBillingFormValid,
    isBillingAddressValid,
    isTaxDataValid,
    isShippingFormValid,
    isShippingAddressValid,
    submitAddress,
    updateSuggestedBillingAddress,
    updateSuggestedShippingAddress,
    setShowAddressModal,
    setCurrentStep,
    setSelectedAddressOption,
    setValidationErrors,
    showBodyLoader,
    hideBodyLoader,
    handleDualAddressSuccess,
    fetchCart,
    t
  });

  const handleFormSubmit = useCallback((ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    const sessionexpiredID = nvCookieHelpers.getCookie("session_expired");
    if (sessionexpiredID && !JSON.parse(sessionexpiredID)) {
      //submitCustomerForm();
      let isLoggedIn = nvCookieHelpers.getCookie("isUserLoggedIn");
      if (isLoggedIn && JSON.parse(isLoggedIn)) {
        submitCustomerForm();
      } else {
        if (savePayment) {
          submitCustomerForm();
        } else {
          if (savePayment || validPayment) {
            if (!savePayment) {
              setValidationErrors((prevErrors) => ({
                ...prevErrors,
                paymentSrc: "Please retry with a different payment method",
              }));
            }
            if (validPayment) {
              setValidationErrors((prevErrors) => ({
                ...prevErrors,
                paymentSrc: "",
              }));
              setSavePayment(true);
            }
          } else {
            setValidationErrors((prevErrors) => ({
              ...prevErrors,
              paymentSrc: "Please retry with a different payment method",
            }));
            ev.preventDefault();
            return;
          }
        }
      }
    } else {
      emptyCart(t("YOUR_SHOPPING_CART_IS_CURRENTLY_EMPTY"));
    }
  }, [submitCustomerForm, emptyCart, t, savePayment, validPayment, setValidationErrors, setSavePayment]);

  // handleShippingAddressUpdate, handleAddressOptionChange, handleContinueToPayment, 
  // and handleBackToAddresses are now provided by useAddressModal hook (removed 160 lines)

  return (
    <div className="User">
      <Container>
        <Form className="orderAddressDetails" autoComplete="none" aria-labelledby="orderAddressDetails">
          <Row xs={"auto"} md={"auto"}>
            <Col md={"7"} sm={"auto"} className="w100" role="region" aria-labelledby="cartSectionHeader">
              <Card id="ShoppingCartMobile">
                <div className={isCartLoading ? `cart-loading` : ""} aria-busy={isCartLoading}>
                  {isCartLoading && (
                    <Image
                      src={cartLoader}
                      className="cartLoader"
                      alt="Loading cart"
                    ></Image>
                  )}
                  {cartData && (
                    <Cart
                      onCartChange={handleCartQuantityChange}
                      validationErrors={validationErrors}
                      onPromoChange={handleCouponChange}
                      onPromoSubmit={handleCouponSubmit}
                      onPromoDelete={handleCouponDelete}
                      isThanks={isThanks}
                      isVerify={isVerify}
                      cartData={cartData}
                      promoCodeError={promoErr}
                    />
                  )}
                </div>
              </Card>
              <div id="newLoginContainer">
                {userType === "loggedIn" && <h1 id="orderAddressDetails">{t("VERIFY_ORDER_HEADER")}</h1>}
                {userType === "guest" && <><h1 id="orderAddressDetails">{t("CHECKOUT_GUEST_BTN")}</h1>
                  <p className="dr_loginInstructions">
                    {t("HAVE_AN_ACCOUNT")}{" "}
                    <Button className="continue" onClick={handleLogin}>
                      {t("SITE_LOGIN_PAGE_TITLE")}
                    </Button>
                  </p></>
                }
              </div>
              <h3 id="cartSectionHeader" className="visually-hidden">{t("SHOPPING_CART_HEADER")}</h3>
              {addressErr && (
                <>
                  <p
                    className={`addressErr ${validationErrors.addressErr ? "is-invalid" : ""
                      }`}
                  ></p>
                  <Form.Control.Feedback type="invalid" id="addressErr" aria-live="polite" aria-describedby="addressErr">
                    {validationErrors.addressErr}
                  </Form.Control.Feedback>
                </>
              )}
              
              {/* STEP 1: Address Information */}
              {currentStep === 1 && (
                <>
              <Row xs={"auto"} md={"auto"} className="mb-2" role="region" aria-labelledby="billingDetailsHeader">
                <h3
                  id="billingDetailsHeader"
                  className={`billingDetails ${validationErrors.register ? "is-invalid" : ""}`} >
                  {t("BILLING_ADDRESS")}
                </h3>
                <Form.Control.Feedback type="invalid" id="billingDetailsError" aria-live="polite">
                  {validationErrors.register}
                </Form.Control.Feedback>
              </Row>
              <Row xs={"auto"} md={"auto"} className=" mb-2" role="region" aria-labelledby="billingDetailsForm" id="billingDetailsForm">
                <Col md={6}>
                  {userType === "loggedIn" &&
                    savedBillingAddress &&
                    savedBillingAddress.length > 0 ? (
                    <Form.Group controlId="savedBillingAddress" id="billingAddressSelect" aria-label="Saved Billing Address" role="region" aria-labelledby="billingAddressSelect">
                      {/* Add a visible label for the select, or use aria-label if not possible */}
                      <label htmlFor="savedBillingAddressSelect" className="visually-hidden">
                        Select Billing Address
                      </label>
                      <Form.Select
                        id="savedBillingAddressSelect"
                        autoComplete="none"
                        value={selectedBillingAddress ? selectedBillingAddress["id"] : ""}
                        name="savedBillingAddress"
                        aria-label="Select Billing Address" // use only if no visible label
                        aria-required="true" // if this is a required field
                        onChange={(e) =>
                          handleSavedBillingAddressChange(
                            "savedBillingAddress",
                            e.target.value
                          )
                        }
                      >
                        {savedBillingAddress?.map((address, index) => (
                          <option
                            key={address["id"] ?? index}
                            value={address["id"]}
                            selected={address["isDefault"]}
                            aria-selected={address["isDefault"]}
                            aria-label={address["nickName"]}
                            aria-describedby="billingAddressSelect"
                          // Remove selected, React handles it via value on <select>
                          >
                            {address["nickName"]}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  ) : null}
                </Col>
                {userType === "loggedIn" &&
                  savedBillingAddress &&
                  savedBillingAddress.length ? (
                  <Col md={6}>
                          <Anchor
                            className="pull-right"
                      onClick={() =>
                        handleSavedBillingAddressChange(
                          "savedBillingAddress",
                          RESET_ADDRESS
                        )
                      }
                      aria-label="Add new address"
                      aria-describedby="addNewAddress"
                      aria-live="polite"
                    >
                            <FaPlus />&#160;{t("ADD_NEW_ADDRESS")}
                          </Anchor>
                    <span id="addNewAddress" className="visually-hidden">
                      {t("ADD_NEW_ADDRESS_DESCRIPTION") || "Add a new billing address to your account"}
                    </span>
                  </Col>
                ) : null}
              </Row>
              <Row xs={"auto"} md={"auto"} className="mb-4">
                {locale !== 'ja-jp' ? (
                  <>
                    <Billing
                      userType={userType}
                      onInputChange={handleBillingInputChange}
                      formBillingDetails={formBillingData}
                      validationErrors={validationErrors}
                      isReadOnly={isBillingReadonly}
                      billingUser={
                        selectedBillingAddress
                          ? JSON.stringify(selectedBillingAddress)
                          : ""
                      }
                    />
                    <Places
                      isThanks={isThanks}
                      isVerify={isVerify}
                      validationErrors={validationErrors}
                      isBillingReadonly={isBillingReadonly}
                      billingAddr={
                        selectedBillingAddress
                          ? JSON.stringify(selectedBillingAddress)
                          : ""
                      }
                      formType="billing"
                      isShippingReadonly={isShippingReadonly}
                      onPlaceUpdate={(data) => handleAddressUpdate(data, "billing")}
                          onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "billing")}
                      brand={brand}
                    />
                  </>
                ) : (
                  <>
                    <BillingJapan
                      userType={userType}
                      onInputChange={handleBillingInputChange}
                      formBillingDetails={formBillingData}
                      validationErrors={validationErrors}
                      isReadOnly={isBillingReadonly}
                      billingUser={
                        selectedBillingAddress
                          ? JSON.stringify(selectedBillingAddress)
                          : ""
                      }
                    />
                    <PlacesJapan
                        isThanks={isThanks}
                      isVerify={isVerify}
                      validationErrors={validationErrors}
                      isBillingReadonly={isBillingReadonly}
                      billingAddr={
                        selectedBillingAddress
                          ? JSON.stringify(selectedBillingAddress)
                          : ""
                      }
                      formType="billing"
                      isShippingReadonly={isShippingReadonly}
                      onPlaceUpdate={(data) => handleAddressUpdate(data, "billing")}
                            onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "billing")}
                    />
                  </>
                )}
              </Row>
              {locale !== 'en-us' && (
                    <BusinessPurchaseSection
                      isBusinessPurchase={isBusinessPurchase}
                      formTaxData={formTaxData}
                      validationErrors={validationErrors}
                      handleisBusinessPurchaseChange={handleisBusinessPurchaseChange}
                      setFormTaxData={setFormTaxData}
                      t={t}
                    />
              )}
              <Row xs={"auto"} md={"auto"} className="mb-4">
                <Col md={12}>
                  <Form.Group className="isShipping" controlId="isShipping">
                    <Form.Check
                      type="checkbox"
                      name="sameShipping"
                      label={t("SHIPPING_ADDRESS_DIFFERENT_BILLING")}
                      checked={isShipping}
                      onChange={handleisShippingChange}
                      aria-checked={isShipping}
                      aria-label={t("SHIPPING_ADDRESS_DIFFERENT_BILLING")}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {isShipping && (
                <>
                  <Row xs={"auto"} md={"auto"} className={shipingInfo} id="shippingDetailsHeader" role="region" aria-labelledby="shippingDetailsHeader" aria-describedby="shippingDetails">
                    <Col md={12} className="p-0">
                      <h3 id="shippingDetailsHeader" role="region" aria-labelledby="shippingDetailsHeader" aria-describedby="shippingDetails" className={`shippingDetails ${validationErrors.shippingAddressErr ? "is-invalid" : ""}`}>{t("SHIPPING_INFORMATION")}</h3>
                      <p className="instructions" id="shippingDetails" role="region" aria-labelledby="shippingDetails" aria-describedby="shippingDetails">{t("SHIPPING_INSTRUCTIONS")}</p>
                      <p className="instructions" id="shippingDetails" role="region" aria-labelledby="shippingDetails" aria-describedby="shippingDetails">{t("REQUIRED_FIELDS_TEXT")}</p>
                      {validationErrors.shippingAddressErr && (
                        <div className="invalid-feedback d-block" id="shippingAddressErr" role="alert" aria-live="polite">
                          {validationErrors.shippingAddressErr}
                        </div>
                      )}
                    </Col>
                  </Row>
                  <Row xs={"auto"} md={"auto"} id="shippingDetailsForm" role="region" aria-labelledby="shippingDetailsForm" aria-describedby="shippingDetailsForm">
                    <Col md={6}>
                      {userType === "loggedIn" &&
                        savedShippingAddress &&
                        savedShippingAddress.length > 0 && (
                          <Form.Group controlId="savedShippingAddress" id="shippingAddressSelect" role="region" aria-labelledby="shippingAddressSelect" aria-describedby="shippingAddressSelect">
                            <Form.Select
                              autoComplete="none"
                              value={
                                selectedShippingAddress
                                  ? selectedShippingAddress["id"]
                                  : ""
                              }
                              name="savedShippingAddress"
                              aria-label="Select Shipping Address"
                              onChange={(e) =>
                                handleSavedShippingAddressChange(
                                  "savedShippingAddress",
                                  e.target.value
                                )
                              }
                              aria-invalid={!!validationErrors.savedShippingAddress}
                              aria-describedby={validationErrors.savedShippingAddress ? "savedShippingAddressError" : undefined}
                              isInvalid={!!validationErrors.savedShippingAddress}
                            >
                              {savedShippingAddress?.map((address, index) => (
                                <option
                                  key={index}
                                  value={address["id"]}
                                  selected={address["isDefault"]}
                                >
                                  {address["nickName"]}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid" id="savedShippingAddressError" aria-live="polite" aria-describedby="savedShippingAddressError">
                              {validationErrors.savedShippingAddress}
                            </Form.Control.Feedback>
                          </Form.Group>
                        )}
                    </Col>
                    {userType === "loggedIn" &&
                      savedShippingAddress &&
                      savedShippingAddress.length > 0 && (
                        <Col md={6}>
                            <Anchor
                              className="pull-right"
                            onClick={() =>
                              handleSavedShippingAddressChange(
                                "savedShippingAddress",
                                RESET_ADDRESS
                              )
                            }
                            aria-label="Add new shipping address"
                            aria-describedby="shippingAddressSelect"
                          >
                              <FaPlus />&#160;{t("ADD_NEW_ADDRESS")}
                            </Anchor>
                        </Col>
                      )}
                  </Row>
                  <Row xs={"auto"} md={"auto"} className="mb-4">
                    <div className="Shipping" id="shippingDetailsForm" role="region" aria-labelledby="shippingDetailsForm" aria-describedby="shippingDetailsForm">
                      {locale === 'ja-jp' && (
                        <Col md={6}>
                          <Form.Group controlId="lastNameShiping">
                            <Form.Label htmlFor="lastNameShiping" className="visually-hidden">{t("LAST_NAME")}</Form.Label>
                            <Form.Control
                              id="lastNameShiping"
                              className={`form-control ${isShippingReadonly ? "disabled" : ""
                                }`}
                              required={isShipping}
                              readOnly={isShippingReadonly}
                              placeholder={t("LAST_NAME_REQUIRED")}
                              type="text"
                              autoComplete="none"
                              aria-required="true"
                              aria-invalid={!!validationErrors.lastNameShiping}
                              aria-describedby={validationErrors.lastNameShiping ? "lastNameShipingError" : undefined}
                              isInvalid={!!validationErrors.lastNameShiping}
                              name="lastNameShiping"
                              value={formData.lastNameShiping}
                              onBlur={(e) =>
                                handleInputChange(
                                  "lastNameShiping",
                                  e.target.value
                                )
                              }
                              onChange={(e) =>
                                handleInputChange(
                                  "lastNameShiping",
                                  e.target.value
                                )
                              }
                            />
                            <Form.Control.Feedback type="invalid" id="lastNameShipingError" aria-live="polite" aria-describedby="lastNameShipingError">
                              {validationErrors.lastNameShiping}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                      <Col md={6}>
                        <Form.Group controlId="firstNameShiping">
                          <Form.Label htmlFor="firstNameShiping" className="visually-hidden">{t("FIRST_NAME")}</Form.Label>
                          <Form.Control
                            id="firstNameShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={isShipping}
                            readOnly={isShippingReadonly}
                            placeholder={t("FIRST_NAME_REQUIRED")}
                            type="text"
                            autoComplete="none"
                            aria-required="true"
                            aria-invalid={!!validationErrors.firstNameShiping}
                            aria-describedby={validationErrors.firstNameShiping ? "firstNameShipingError" : undefined}
                            isInvalid={!!validationErrors.firstNameShiping}
                            name="firstNameShiping"
                            value={formData.firstNameShiping}
                            onBlur={(e) =>
                              handleInputChange(
                                "firstNameShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "firstNameShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="firstNameShipingError" aria-live="polite" aria-describedby="firstNameShipingError">
                            {validationErrors.firstNameShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {locale !== 'ja-jp' && (<Col md={6}>
                        <Form.Group controlId="lastNameShiping">
                          <Form.Label htmlFor="lastNameShiping" className="visually-hidden">{t("LAST_NAME")}</Form.Label>
                          <Form.Control
                            id="lastNameShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={isShipping}
                            readOnly={isShippingReadonly}
                            placeholder={t("LAST_NAME_REQUIRED")}
                            type="text"
                            autoComplete="none"
                            aria-required="true"
                            aria-invalid={!!validationErrors.lastNameShiping}
                            aria-describedby={validationErrors.lastNameShiping ? "lastNameShipingError" : undefined}
                            isInvalid={!!validationErrors.lastNameShiping}
                            name="lastNameShiping"
                            value={formData.lastNameShiping}
                            onBlur={(e) =>
                              handleInputChange(
                                "lastNameShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "lastNameShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="lastNameShipingError" aria-live="polite" aria-describedby="lastNameShipingError">
                            {validationErrors.lastNameShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>)}
                      <Col md={6}>
                        <Form.Group controlId="contactShiping">
                          <Form.Label htmlFor="contactShiping" className="visually-hidden">{t("PHONE_NUMBER")}</Form.Label>
                          <Form.Control
                            id="contactShiping"
                            className={`form-control ${isShippingReadonly ? "disabled" : ""
                              }`}
                            required={isShipping}
                            readOnly={isShippingReadonly}
                            placeholder={t("PHONE_NUMBER_REQUIRED")}
                            type="text"
                            autoComplete="none"
                            aria-required="true"
                            aria-invalid={!!validationErrors.contactShiping}
                            aria-describedby={validationErrors.contactShiping ? "contactShipingError" : undefined}
                            isInvalid={!!validationErrors.contactShiping}
                            name="contactShiping"
                            value={formatPhoneNo(formData.contactShiping)}
                            onBlur={(e) =>
                              handleInputChange(
                                "contactShiping",
                                e.target.value
                              )
                            }
                            onChange={(e) =>
                              handleInputChange(
                                "contactShiping",
                                e.target.value
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid" id="contactShipingError" aria-live="polite" aria-describedby="contactShipingError">
                            {validationErrors.contactShiping}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </div>
                    {locale !== 'ja-jp' ? (<Places
                      isThanks={isThanks}
                      isVerify={isVerify}
                      validationErrors={validationErrors}
                      isShippingReadonly={isShippingReadonly}
                      billingAddr={
                        selectedShippingAddress
                          ? JSON.stringify(selectedShippingAddress)
                          : ""
                      }
                      formType="shipping"
                      isBillingReadonly={isBillingReadonly}
                      onPlaceUpdate={(data) =>
                        handleAddressUpdate(data, "shipping")
                      }
                          onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "shipping")}
                      onUserAddressChange={handleUserShippingAddressChange}
                      brand={brand}
                    />
                    ) : (
                      <PlacesJapan
                          isThanks={isThanks}
                        isVerify={isVerify}
                        validationErrors={validationErrors}
                        isShippingReadonly={isShippingReadonly}
                        billingAddr={
                          selectedShippingAddress
                            ? JSON.stringify(selectedShippingAddress)
                            : ""
                        }
                        formType="shipping"
                        isBillingReadonly={isBillingReadonly}
                        onPlaceUpdate={(data) =>
                          handleAddressUpdate(data, "shipping")
                        }
                              onAddressBlur={(fieldName, value) => handleAddressBlur(fieldName, value, "shipping")}
                        onUserAddressChange={handleUserShippingAddressChange}
                      />
                    )}
                  </Row>
                </>
              )}
              
              {/* Step 1 Navigation */}
              <Row xs={"auto"} md={"auto"} className="mb-4">
                <Col md={12} className="d-flex justify-content-end">
                      <Button
                        type="button"
                        variant="success"
                        size="lg"
                        className="btn-green continue-button"
                        onClick={handleContinueToPayment}
                        disabled={!isContinueButtonEnabled}
                        aria-label={t("CONTINUE_BTN")}
                      >
                        {t('CONTINUE_BTN')}
                      </Button>
                </Col>
              </Row>
              </>
              )}
              
              {/* STEP 2: Payment & Shipping Method */}
              {currentStep === 2 && (
                <CheckoutStep2
                  cartData={cartData}
                  shippingList={shippingList}
                  shippingOptions={shippingOptions}
                  validationErrors={validationErrors}
                  handleShippingMethodChange={handleShippingMethodChange}
                  selectedBillingAddress={selectedBillingAddress}
                  savePayment={savePayment}
                  formBillingAddress={formBillingAddress}
                  formBillingData={formBillingData}
                  handleFormSubmit={handleFormSubmit}
                  enableReviewOrderBtn={enableReviewOrderBtn}
                  handleBackToAddresses={handleBackToAddresses}
                />
              )}
            </Col>
            <Col md={"5"} sm={"auto"} className="w100">
              <Card id="ShoppingCart">
                <div className={isCartLoading ? `cart-loading` : ""}>
                  {isCartLoading && (
                    <Image
                      src={cartLoader}
                      className="cartLoader"
                      alt="CartLoader"
                    ></Image>
                  )}
                  {cartData && (
                    <Cart
                      onCartChange={handleCartQuantityChange}
                      validationErrors={validationErrors}
                      onPromoChange={handleCouponChange}
                      onPromoSubmit={handleCouponSubmit}
                      onPromoDelete={handleCouponDelete}
                      isThanks={isThanks}
                      isVerify={isVerify}
                      cartData={cartData}
                      promoCodeError={promoErr}
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
      {showAddressModal && (
        <AddressModal
          show={showAddressModal}
          onHide={() => setShowAddressModal(false)}
          selectedAddressOption={selectedAddressOption}
          temporaryAddress={temporaryAddress}
          recommendedShippingAddress={recommendedShippingAddress}
          addressValidationResult={addressValidationResult}
          formShippingAddress={formShippingAddress}
          onAddressOptionChange={handleAddressOptionChange}
          onShippingAddressUpdate={handleShippingAddressUpdate}
          onConfirmAddress={handleConfirmAddress}
          // Dual address props
          isShipping={isShipping}
          temporaryBillingAddress={temporaryBillingAddress}
          recommendedBillingAddress={recommendedBillingAddress}
          selectedBillingAddressOption={selectedBillingAddressOption}
          formBillingAddress={formBillingAddress}
          onBillingAddressOptionChange={(e, option) => setSelectedBillingAddressOption(option as "yourAddress" | "recommendedAddress")}
          billingValidationResult={billingValidationResult}
          shippingValidationResult={shippingValidationResult}
          // Differences for highlighting
          billingAddressDifferences={billingAddressDifferences}
          shippingAddressDifferences={shippingAddressDifferences}
        />
      )}
    </div>
  );
};
export default User;
